"use client";

import { Button } from "@/components/ui/button";
import { BellRing, Menu, X } from "lucide-react";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { createClientComponentClient } from "@supabase/auth-helpers-nextjs";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export function Navbar() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const supabase = createClientComponentClient();
  const isWaitlistEnabled = process.env.NEXT_PUBLIC_WAITLIST_ENABLED === 'true';

  useEffect(() => {
    async function getUser() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        setLoading(false);
      }
    }

    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase]);

  // Hide navbar on protected routes
  if (pathname?.startsWith('/dashboard') ||
      pathname?.startsWith('/settings') ||
      pathname?.startsWith('/history') ||
      pathname?.startsWith('/artists') ||
      pathname?.startsWith('/admin') ||
      pathname?.includes('admin')) {
    return null;
  }

  const handleAuthClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isWaitlistEnabled) {
      router.push('/waitlist');
    } else {
      router.push('/sign-in');
    }
  };

  return (
    <nav className="fixed top-0 w-full z-50 border-b bg-background/80 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <Link href="/" className="flex items-center space-x-3 group">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary to-purple-600 rounded-full blur opacity-40 group-hover:opacity-75 transition"></div>
              <BellRing className="h-8 w-8 text-primary relative" />
            </div>
            <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-400">
              LeakNoti
            </span>
          </Link>
          
          {/* Mobile menu button */}
          <div className="flex md:hidden">
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-400"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link href="#features" className="text-muted-foreground hover:text-primary transition-colors">
              Features
            </Link>
            <Link href="#pricing" className="text-muted-foreground hover:text-primary transition-colors">
              Pricing
            </Link>
            {!loading && (
              user ? (
                <Button size="lg" className="text-base px-6" asChild>
                  <Link href="/dashboard">Dashboard</Link>
                </Button>
              ) : (
                <>
                  <Button variant="ghost" className="text-base" onClick={handleAuthClick}>
                    Sign In
                  </Button>
                  <Link
                    href={isWaitlistEnabled ? "/waitlist" : "/sign-in"}
                    className={cn(
                      buttonVariants({ variant: "default", size: "sm" }),
                      "px-4"
                    )}
                  >
                    {isWaitlistEnabled ? "Join Waitlist" : "Get Started"}
                  </Link>
                </>
              )
            )}
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`md:hidden transition-all duration-300 ease-in-out ${
            mobileMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
          } overflow-hidden`}
        >
          <div className="pb-4 space-y-4">
            <Link
              href="#features"
              className="block px-4 py-2 text-muted-foreground hover:text-primary transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Features
            </Link>
            <Link
              href="#pricing"
              className="block px-4 py-2 text-muted-foreground hover:text-primary transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Pricing
            </Link>
            {!loading && (
              user ? (
                <div className="px-4">
                  <Button size="lg" className="w-full text-base" asChild>
                    <Link href="/dashboard">Dashboard</Link>
                  </Button>
                </div>
              ) : (
                <div className="px-4 space-y-2">
                  <Button variant="ghost" className="w-full text-base" onClick={handleAuthClick}>
                    Sign In
                  </Button>
                  <Link
                    href={isWaitlistEnabled ? "/waitlist" : "/sign-in"}
                    className={cn(
                      buttonVariants({ variant: "default", size: "sm" }),
                      "w-full"
                    )}
                  >
                    {isWaitlistEnabled ? "Join Waitlist" : "Get Started"}
                  </Link>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}